<template>
  <div class="portefeuille-container">
    <div class="portefeuille-shell">
      <div class="portefeuille-header">
        <h1 class="portefeuille-title">{{ $t('portefeuille.title') }}</h1>
        <p class="portefeuille-subtitle">
          {{ $t('portefeuille.subtitle') }}
        </p>
      </div>

      <div class="portefeuille-grid">
        <button
          v-for="card in dashboardCards"
          :key="card.key"
          type="button"
          class="portefeuille-card"
          @click="card.action"
        >
          <div class="card-top">
            <div class="card-icon-badge" :class="card.badgeClass">
              <v-icon size="20" :color="card.iconColor">{{ card.icon }}</v-icon>
            </div>
            <div class="card-count">{{ card.count }}</div>
          </div>

          <div class="card-bottom">
            <h2 class="card-title">{{ card.title }}</h2>
            <p class="card-description">{{ card.description }}</p>
          </div>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
  name: 'Portefeuille',
  computed: {
    ...mapGetters('vehicles', { vehiclesCount: 'count' }),
    ...mapGetters('housings', { housingsCount: 'count' }),
    ...mapGetters('beneficiaries', { beneficiariesCount: 'count' }),
    ...mapGetters('documents', { documentsCount: 'count' }),
    dashboardCards() {
      return [
        {
          key: 'vehicles',
          title: this.$t('portefeuille.vehicles'),
          description: this.$t('portefeuille.vehicles_desc'),
          count: this.vehiclesCount,
          icon: 'directions_car',
          iconColor: '#f5a623',
          badgeClass: 'badge-warm',
          action: this.navigateToVehicles
        },
        {
          key: 'housings',
          title: this.$t('portefeuille.housings'),
          description: this.$t('portefeuille.housings_desc'),
          count: this.housingsCount,
          icon: 'home',
          iconColor: '#6ea6d7',
          badgeClass: 'badge-cool',
          action: this.navigateToHousings
        },
        {
          key: 'beneficiaries',
          title: this.$t('portefeuille.beneficiaries'),
          description: this.$t('portefeuille.beneficiaries_desc'),
          count: this.beneficiariesCount,
          icon: 'groups',
          iconColor: '#5f6168',
          badgeClass: 'badge-neutral',
          action: this.navigateToBeneficiaries
        },
        {
          key: 'documents',
          title: this.$t('portefeuille.documents'),
          description: this.$t('portefeuille.documents_desc'),
          count: this.documentsCount,
          icon: 'description',
          iconColor: '#f5a623',
          badgeClass: 'badge-soft',
          action: this.navigateToDocuments
        }
      ]
    }
  },
  mounted() {
    this.loadPortfolioData()
  },
  methods: {
    loadPortfolioData() {
      this.$store.dispatch('vehicles/fetchVehicles').catch(() => {})
      this.$store.dispatch('housings/fetchHousings').catch(() => {})
      this.$store.dispatch('beneficiaries/fetchBeneficiaries').catch(() => {})
      this.$store.dispatch('documents/fetchDocuments').catch(() => {})
    },
    navigateToVehicles() {
      this.$router.push({ name: 'portefeuille-vehicles' })
    },
    navigateToHousings() {
      this.$router.push({ name: 'portefeuille-housings' })
    },
    navigateToBeneficiaries() {
      this.$router.push({ name: 'portefeuille-beneficiaries' })
    },
    navigateToDocuments() {
      this.$router.push({ name: 'portefeuille-documents' })
    }
  }
}
</script>

<style scoped>
.portefeuille-container {
  width: 100%;
  min-height: 100vh;
  padding: 88px 24px 48px;
  background: #f5f6fa;
}

.portefeuille-shell {
  max-width: 980px;
  margin: 0 auto;
}

.portefeuille-header {
  margin-bottom: 34px;
  padding-left: 2px;
}

.portefeuille-title {
  margin: 0 0 10px;
  color: #182033;
  font-size: 32px;
  font-weight: 600;
  line-height: 1.1;
  letter-spacing: -0.03em;
  font-family: 'Poppins', 'Lato', sans-serif;
}

.portefeuille-subtitle {
  max-width: 760px;
  margin: 0;
  color: #70798b;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.6;
  font-family: 'Poppins', 'Lato', sans-serif;
}

.portefeuille-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 24px 28px;
}

.portefeuille-card {
  min-height: 176px;
  padding: 24px 24px 22px;
  border: 2px solid transparent;
  border-radius: 24px;
  background: #fcfdff;
  box-shadow: 0 8px 28px rgba(20, 31, 56, 0.04);
  text-align: left;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  font-family: 'Poppins', 'Lato', sans-serif;
}

.portefeuille-card:hover {
  transform: translateY(-2px);
  border-color: #f5a623;
  box-shadow: 0 14px 32px rgba(245, 166, 35, 0.12);
}

.card-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
}

.card-icon-badge {
  width: 46px;
  height: 46px;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}


.badge-warm {
  background: #fff7e8;
}

.badge-cool {
  background: #eef6ff;
}

.badge-neutral {
  background: #f5f6f9;
}

.badge-soft {
  background: #fff8ec;
}

.card-count {
  min-width: 28px;
  color: #555b66;
  font-size: 18px;
  font-weight: 700;
  line-height: 1;
  text-align: right;
  letter-spacing: -0.03em;
}

.portefeuille-card:hover .card-count {
  color: #f5a623;
}

.card-bottom {
  margin-top: 22px;
}

.card-title {
  margin: 0 0 8px;
  color: #182033;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.35;
  font-family: 'Poppins', 'Lato', sans-serif;
}

.card-description {
  margin: 0;
  color: #6f7788;
  font-size: 13px;
  font-weight: 400;
  line-height: 1.55;
  font-family: 'Poppins', 'Lato', sans-serif;
}

@media only screen and (max-width: 1024px) {
  .portefeuille-title {
    font-size: 28px;
  }

  .portefeuille-subtitle {
    font-size: 13px;
  }
}

@media only screen and (max-width: 768px) {
  .portefeuille-container {
    padding: 80px 16px 32px;
  }

  .portefeuille-grid {
    grid-template-columns: 1fr;
    gap: 18px;
  }

  .portefeuille-title {
    font-size: 28px;
  }

  .portefeuille-subtitle {
    font-size: 15px;
  }

  .card-title {
    font-size: 18px;
  }

  .card-description {
    font-size: 15px;
  }

  .card-count {
    font-size: 20px;
  }

  .portefeuille-card {
    min-height: 160px;
    padding: 22px 20px;
  }
}

@media only screen and (max-width: 480px) {
  .portefeuille-container {
    padding-top: 72px;
  }

  .portefeuille-title {
    font-size: 26px;
  }

  .portefeuille-header {
    margin-bottom: 24px;
  }

  .card-icon-badge {
    width: 44px;
    height: 44px;
    border-radius: 12px;
  }

  .card-title {
    font-size: 17px;
  }

  .card-description {
    font-size: 15px;
  }
}
</style>
